import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Assignment, AssignmentCategory, ClassMember, User } from '../types/alarm';
import { generateAssignmentId } from '../utils/idGenerator';
import { ItemCard } from '../types/itemCard';
import { ItemCardService } from '../services/itemCardService';
import { checkAssignmentLimit, isStandardPlanOrAbove } from '../utils/licenseLimits';
import { assignmentService } from '../services/assignmentService';
import { userDataService } from '../services/userDataService';
import LicenseLimitModal from './LicenseLimitModal';
import ConfirmModal from './ConfirmModal';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';

interface NewAssignmentModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (assignment: Assignment, selectedMemberIdsByGroup: Record<string, string[]>, mode: 'new' | 'copy' | 'edit') => void;
  members: ClassMember[];
  mode?: 'new' | 'copy' | 'edit';
  initialAssignment?: Partial<Assignment>;
  initialSelectedIds?: string[];
  categories: AssignmentCategory[];
  defaultPoints?: number; // 기본 단위 포인트
  userId: string;
  classId: string;
  user: User; // 사용자 정보 추가 (라이센스 체크용)
  onOpenItemCardModal?: (currentItemCardIds: string[], onConfirm: (selectedIds: string[]) => void) => void;
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
  memberGroups?: string[];
  memberGroupOrder?: string[];
  currentMemberGroup?: string;
  memberGroupNames?: { [groupKey: string]: string };
}

const NewAssignmentModal: React.FC<NewAssignmentModalProps> = ({ isOpen, onCancel, onSave, members, mode = 'new', initialAssignment, initialSelectedIds, categories, defaultPoints = 10, userId, classId, user, onOpenItemCardModal, onLicenseClick, memberGroups, memberGroupOrder, currentMemberGroup, memberGroupNames }) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [basePointsDisplay, setBasePointsDisplay] = useState<string>(() => String(defaultPoints));
  const getBasePointsNum = () => (basePointsDisplay === '' ? 0 : (Number(basePointsDisplay) || 0));
  const [categoryId, setCategoryId] = useState<string>('');
  const [icon, setIcon] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const normalizedGroups = useMemo(() => {
    let base = getOrderedMemberGroups(memberGroups, memberGroupOrder);

    // 프리·베이직은 기본 그룹(A)만, 스탠다드 이상은 설정된 그룹 사용
    if (!isStandardPlanOrAbove(user.userType)) {
      base = ['A'];
    }

    return base;
  }, [memberGroups, memberGroupOrder, user.userType]);
  const primaryGroup = useMemo(() => {
    const g = (currentMemberGroup || normalizedGroups[0] || 'A').toString().trim().toUpperCase();
    return normalizedGroups.includes(g) ? g : (normalizedGroups[0] || 'A');
  }, [currentMemberGroup, normalizedGroups]);

  const [activeGroup, setActiveGroup] = useState<string>('A');
  // NOTE: 전파 여부는 "각 그룹에서 학생을 1명이라도 선택했는지"로 판단한다.
  const [membersByGroup, setMembersByGroup] = useState<Record<string, ClassMember[]>>({});
  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, Set<string>>>({});
  const [selectionLoadedGroups, setSelectionLoadedGroups] = useState<Set<string>>(new Set());
  const [hoveredStudent, setHoveredStudent] = useState<{id: string, name: string} | null>(null);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  
  // 과제 보상 관련 상태
  const [rewardEnabled, setRewardEnabled] = useState(false);
  const [rewardCode, setRewardCode] = useState('');
  const [rewardMessage, setRewardMessage] = useState('');
  const [rewardCoinsDisplay, setRewardCoinsDisplay] = useState<string>('0');
  const getRewardCoinsNum = () => (rewardCoinsDisplay === '' ? 0 : (Number(rewardCoinsDisplay) || 0));
  const [rewardItemCardIds, setRewardItemCardIds] = useState<string[]>([]);
  const [selectedRewardCards, setSelectedRewardCards] = useState<ItemCard[]>([]);
  const [rewardCardImageErrors, setRewardCardImageErrors] = useState<Set<string>>(new Set());
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const getGroupDisplayName = useCallback((groupKey: string): string => {
    return memberGroupNames?.[groupKey] || `${groupKey}그룹`;
  }, [memberGroupNames]);

  // 초기 상태 저장용
  const [initialState, setInitialState] = useState({
    title: '',
    dueDate: '',
    basePoints: defaultPoints,
    categoryId: '',
    icon: '',
    notes: '',
    selectedByGroup: {} as Record<string, Set<string>>,
    rewardEnabled: false,
    rewardCode: '',
    rewardMessage: '',
    rewardCoins: 0,
    rewardItemCardIds: [] as string[]
  });

  // 변경사항 확인 함수
  const hasUnsavedChanges = useMemo(() => {
    // 초기 로딩이 완료되지 않은 경우 변경사항 없음으로 처리
    if (!isInitialLoadComplete) return false;

    // 기본 정보 변경 체크
    const hasBasicChanges =
      title !== initialState.title ||
      dueDate !== initialState.dueDate ||
      getBasePointsNum() !== initialState.basePoints ||
      categoryId !== initialState.categoryId ||
      icon !== initialState.icon ||
      notes !== initialState.notes;

    // 학생 선택 변경 체크
    const currentSelectedIds = Object.values(selectedByGroup).flatMap(set => Array.from(set));
    const initialSelectedIds = Object.values(initialState.selectedByGroup).flatMap(set => Array.from(set));
    const hasSelectionChanges =
      currentSelectedIds.length !== initialSelectedIds.length ||
      !currentSelectedIds.every(id => initialSelectedIds.includes(id)) ||
      !initialSelectedIds.every(id => currentSelectedIds.includes(id));

    // 보상 설정 변경 체크
    const hasRewardChanges =
      rewardEnabled !== initialState.rewardEnabled ||
      rewardCode !== initialState.rewardCode ||
      rewardMessage !== initialState.rewardMessage ||
      getRewardCoinsNum() !== initialState.rewardCoins ||
      JSON.stringify(rewardItemCardIds.sort()) !== JSON.stringify(initialState.rewardItemCardIds.sort());

    return hasBasicChanges || hasSelectionChanges || hasRewardChanges;
  }, [
    title, dueDate, basePointsDisplay, categoryId, icon, notes,
    selectedByGroup, initialState,
    rewardEnabled, rewardCode, rewardMessage, rewardCoinsDisplay, rewardItemCardIds,
    isInitialLoadComplete
  ]);

  // 모달 닫기 핸들러 (변경사항 확인 포함)
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  // 보상 카드 정보 로드 함수
  const loadRewardCards = useCallback(async (itemCardIds: string[]) => {
    if (itemCardIds.length === 0) {
      setSelectedRewardCards([]);
      setRewardCardImageErrors(new Set()); // 이미지 에러 초기화
      return;
    }

    try {
      const itemCardService = new ItemCardService(userId, classId);
      const itemCardSet = await itemCardService.loadItemCardSet();
      
      if (itemCardSet) {
        const allItems = [...itemCardSet.levelupItem, ...itemCardSet.assignmentItem, ...itemCardSet.storeItem];
        const selectedItems = allItems.filter(item => itemCardIds.includes(item.itemCardId));
        setSelectedRewardCards(selectedItems);
        setRewardCardImageErrors(new Set()); // 카드 정보 로드 시 이미지 에러 초기화
      }
    } catch (error) {
      console.error('보상 카드 정보 로드 실패:', error);
      setSelectedRewardCards([]);
      setRewardCardImageErrors(new Set());
    }
  }, [userId, classId]);

  // rewardItemCardIds가 변경될 때마다 카드 정보 로드
  useEffect(() => {
    if (rewardItemCardIds.length > 0) {
      loadRewardCards(rewardItemCardIds);
    } else {
      setSelectedRewardCards([]);
    }
  }, [rewardItemCardIds, loadRewardCards]);

  useEffect(() => {
    if (isOpen) {
      // 모달 열릴 때 상태 초기화
      setShowConfirmModal(false);
      document.body.style.overflow = 'hidden';
      // 오늘 날짜 기본값
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;

      console.log('🔍 NewAssignmentModal - 모달 열림, initialAssignment:', initialAssignment);
      console.log('🔍 NewAssignmentModal - icon 값:', initialAssignment?.icon);
      console.log('🔍 NewAssignmentModal - notes 값:', initialAssignment?.notes);
      
      setTitle(initialAssignment?.title ?? '');
      const initDue = initialAssignment?.dueDate ? new Date(typeof initialAssignment.dueDate === 'string' || typeof initialAssignment.dueDate === 'number' ? new Date(initialAssignment.dueDate) : (initialAssignment.dueDate as Date)) : new Date(todayStr);
      setDueDate(isNaN(initDue.getTime()) ? todayStr : initDue.toISOString().slice(0,10));
      setBasePointsDisplay(initialAssignment?.basePoints != null ? String(initialAssignment.basePoints) : String(defaultPoints));
      setCategoryId(initialAssignment?.categoryId ?? '');
      setIcon(initialAssignment?.icon ?? '');
      setNotes(initialAssignment?.notes ?? '');
      
      // 과제 보상 관련 필드 초기화
      setRewardEnabled(initialAssignment?.rewardEnabled ?? false);
      setRewardCode(initialAssignment?.rewardCode ?? '');
      setRewardMessage(initialAssignment?.rewardMessage ?? '');
      setRewardCoinsDisplay(initialAssignment?.rewardCoins != null ? String(initialAssignment.rewardCoins) : '0');
      setRewardItemCardIds(initialAssignment?.rewardItemCardIds ?? []);
      
      // 보상 카드 정보 로드
      if (initialAssignment?.rewardItemCardIds && initialAssignment.rewardItemCardIds.length > 0) {
        loadRewardCards(initialAssignment.rewardItemCardIds);
      } else {
        setSelectedRewardCards([]);
      }
      
      console.log('🔍 NewAssignmentModal - 상태 설정 완료:');
      console.log('🔍 NewAssignmentModal - setIcon:', initialAssignment?.icon ?? '');
      console.log('🔍 NewAssignmentModal - setNotes:', initialAssignment?.notes ?? '');
      const initialIds = initialSelectedIds && initialSelectedIds.length > 0 ? initialSelectedIds : members.map(m => m.id);
      setActiveGroup(primaryGroup);
      setMembersByGroup({ [primaryGroup]: members });
      setSelectedByGroup({ [primaryGroup]: new Set(initialIds) });
      setSelectionLoadedGroups(new Set([primaryGroup]));
      setIsCategoryDropdownOpen(false);

      // 초기 상태 저장
      setInitialState({
        title: initialAssignment?.title ?? '',
        dueDate: initDue.toISOString().slice(0, 10),
        basePoints: initialAssignment?.basePoints ?? defaultPoints,
        categoryId: initialAssignment?.categoryId ?? '',
        icon: initialAssignment?.icon ?? '',
        notes: initialAssignment?.notes ?? '',
        selectedByGroup: { [primaryGroup]: new Set(initialIds) },
        rewardEnabled: initialAssignment?.rewardEnabled ?? false,
        rewardCode: initialAssignment?.rewardCode ?? '',
        rewardMessage: initialAssignment?.rewardMessage ?? '',
        rewardCoins: initialAssignment?.rewardCoins ?? 0,
        rewardItemCardIds: initialAssignment?.rewardItemCardIds ?? []
      });

      // 초기 로딩 완료 표시 (즉시 설정)
      setIsInitialLoadComplete(true);
    } else {
      document.body.style.overflow = 'auto';
      setIsCategoryDropdownOpen(false);
      // 모달이 닫힐 때 초기 상태 리셋
      setIsInitialLoadComplete(false);
      setInitialState({
        title: '',
        dueDate: '',
        basePoints: defaultPoints,
        categoryId: '',
        icon: '',
        notes: '',
        selectedByGroup: {},
        rewardEnabled: false,
        rewardCode: '',
        rewardMessage: '',
        rewardCoins: 0,
        rewardItemCardIds: []
      });
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen, members, initialAssignment, initialSelectedIds, mode, primaryGroup, defaultPoints, loadRewardCards]);

  // members가 변경될 때마다 selected 상태 업데이트
  useEffect(() => {
    if (!isOpen) return;
    // primaryGroup 멤버 목록이 갱신되면 selectedByGroup[primaryGroup]도 유효성 맞춤
    setMembersByGroup(prev => ({ ...prev, [primaryGroup]: members }));
    setSelectedByGroup(prev => {
      const current = prev[primaryGroup] || new Set<string>();
      const valid = new Set(Array.from(current).filter(id => members.some(m => m.id === id)));
      const shouldResetToAll = (initialSelectedIds && initialSelectedIds.length === 0 && valid.size === 0);
      if (valid.size !== current.size || shouldResetToAll) {
        const nextIds = (initialSelectedIds && initialSelectedIds.length > 0)
          ? initialSelectedIds.filter(id => members.some(m => m.id === id))
          : members.map(m => m.id);
        return { ...prev, [primaryGroup]: new Set(nextIds) };
      }
      return prev;
    });
  }, [members, isOpen, initialSelectedIds, primaryGroup]);

  // 모달이 열릴 때마다 최신 members 데이터로 selected 상태 초기화
  useEffect(() => {
    if (!isOpen) return;
    if (members.length === 0) return;
    const newSelectedIds = initialSelectedIds && initialSelectedIds.length > 0 
      ? initialSelectedIds.filter(id => members.some(m => m.id === id))
      : members.map(m => m.id);
    setSelectedByGroup(prev => ({ ...prev, [primaryGroup]: new Set(newSelectedIds) }));
  }, [isOpen, members, initialSelectedIds, primaryGroup]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (hoveredStudent) {
      timer = setTimeout(() => {
        setHoveredStudent(null);
      }, 2000);
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [hoveredStudent]);

  const activeMembers = useMemo(() => membersByGroup[activeGroup] || [], [membersByGroup, activeGroup]);
  const activeSelected = useMemo(() => selectedByGroup[activeGroup] || new Set<string>(), [selectedByGroup, activeGroup]);

  const toggleAll = () => {
    setSelectedByGroup(prev => {
      const current = prev[activeGroup] || new Set<string>();
      const allIds = activeMembers.map(m => m.id);
      const next = (current.size === allIds.length) ? new Set<string>() : new Set(allIds);
      return { ...prev, [activeGroup]: next };
    });
  };

  // 과제명 완성 시 카테고리 자동 매칭
  const handleTitleComplete = (title: string) => {
    if (title.length < 3) return; // 최소 길이 체크
    
    // 카테고리명이 과제명에 포함되어 있는지 확인 (더 구체적인 카테고리명 우선)
    // 앞뒤 빈칸을 무시하고 매칭
    const matchedCategory = categories
      .filter(category => {
        const trimmedCategoryName = category.name.trim();
        return title.toLowerCase().includes(trimmedCategoryName.toLowerCase());
      })
      .sort((a, b) => b.name.trim().length - a.name.trim().length)[0]; // 긴 카테고리명 우선
    
    if (matchedCategory) {
      setCategoryId(matchedCategory.id);
      console.log(`자동으로 '${matchedCategory.name.trim()}' 카테고리가 선택되었습니다.`);
    }
  };

  // 순환 탭 처리
  const handleTabKey = (e: React.KeyboardEvent, currentTabIndex: number) => {
    if (e.key === 'Tab') {
      // Tab 키의 기본 동작을 방해하지 않음
      // tabIndex가 올바르게 설정되어 있으면 자동으로 다음 필드로 이동
      return;
    }
  };

  // 카테고리 선택 완료 후 다음 필드로 이동
  const handleCategorySelect = (categoryId: string) => {
    setCategoryId(categoryId);
    setIsCategoryDropdownOpen(false);
    // 다음 필드로 이동
    const nextElement = document.querySelector('[tabindex="4"]') as HTMLElement;
    if (nextElement) nextElement.focus();
  };

  // 선택된 카테고리 인덱스가 변경될 때 스크롤 자동 조정
  useEffect(() => {
    if (isCategoryDropdownOpen && selectedCategoryIndex >= 0) {
      const dropdown = document.querySelector('.category-dropdown') as HTMLElement;
      if (dropdown) {
        const selectedOption = dropdown.children[selectedCategoryIndex] as HTMLElement;
        if (selectedOption) {
          selectedOption.scrollIntoView({ 
            block: 'nearest', 
            behavior: 'smooth' 
          });
        }
      }
    }
  }, [selectedCategoryIndex, isCategoryDropdownOpen]);

  const toggle = (id: string) => {
    setSelectedByGroup(prev => {
      const current = new Set(prev[activeGroup] || []);
      if (current.has(id)) current.delete(id); else current.add(id);
      return { ...prev, [activeGroup]: current };
    });
  };

  const ensureMembersLoaded = useCallback(async (group: string) => {
    const g = (group || 'A').toString().trim().toUpperCase();
    if (membersByGroup[g]) return;
    try {
      const loaded = await userDataService.loadClassMembers(userId, classId, g);
      setMembersByGroup(prev => ({ ...prev, [g]: loaded }));
      setSelectedByGroup(prev => ({ ...prev, [g]: prev[g] || new Set<string>() }));
    } catch (e) {
      console.error('멤버 그룹 로드 실패:', e);
      setMembersByGroup(prev => ({ ...prev, [g]: [] }));
      setSelectedByGroup(prev => ({ ...prev, [g]: prev[g] || new Set<string>() }));
    }
  }, [membersByGroup, userId, classId]);

  const ensureAssignmentSelectionLoaded = useCallback(async (group: string) => {
    const g = (group || 'A').toString().trim().toUpperCase();
    if (mode !== 'edit') return;
    const assignmentId = initialAssignment?.id as string | undefined;
    if (!assignmentId) return;
    if (selectionLoadedGroups.has(g)) return;

    try {
      const assignments = await assignmentService.loadAssignments(userId, classId, g);
      const found = assignments.find(a => a.id === assignmentId);
      const ids = Array.isArray(found?.assignedStudentIds) ? found!.assignedStudentIds : [];
      setSelectedByGroup(prev => ({ ...prev, [g]: new Set(ids) }));
      setSelectionLoadedGroups(prev => {
        const next = new Set(prev);
        next.add(g);
        return next;
      });
    } catch (e) {
      console.error('그룹 과제 선택 로드 실패:', e);
      // 실패하더라도 무한 재시도를 막기 위해 마킹
      setSelectionLoadedGroups(prev => {
        const next = new Set(prev);
        next.add(g);
        return next;
      });
    }
  }, [mode, initialAssignment?.id, selectionLoadedGroups, userId, classId]);

  const getSelectedGroupsPayload = useCallback((): Record<string, string[]> => {
    const payload: Record<string, string[]> = {};
    normalizedGroups.forEach((g) => {
      const selectedSet = selectedByGroup[g];
      if (selectedSet && selectedSet.size > 0) {
        payload[g] = Array.from(selectedSet);
      }
    });
    return payload;
  }, [normalizedGroups, selectedByGroup]);

  const handleSave = async () => {
    if (!title || !dueDate) return;
    
    // 새 과제 생성 시에만 월별 제한 체크 (편집/복사는 제외)
    if (mode === 'new') {
      try {
        // 현재 월의 과제 수 조회
        const allAssignments = await assignmentService.loadAssignments(userId, classId, primaryGroup);
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        const currentMonthAssignments = allAssignments.filter(assignment => {
          const createdAt = assignment.createdAt instanceof Date 
            ? assignment.createdAt 
            : new Date(assignment.createdAt);
          return createdAt.getMonth() === currentMonth && 
                 createdAt.getFullYear() === currentYear;
        });
        
        const limitCheck = checkAssignmentLimit(user, currentMonthAssignments.length);
        if (!limitCheck.allowed) {
          setLicenseLimitMessage(limitCheck.message || '');
          setShowLicenseLimitModal(true);
          return;
        }
      } catch (error) {
        console.error('월별 과제 수 체크 실패:', error);
        // 에러가 발생해도 과제 생성은 진행 (제한 체크 실패는 경고만)
      }
    }
    
    // icon과 notes가 빈 문자열이거나 undefined일 때는 필드를 제외
    const assignmentData: any = {
      id: mode === 'edit' && initialAssignment?.id ? (initialAssignment.id as string) : generateAssignmentId('general'),
      title,
      dueDate: new Date(dueDate),
      basePoints: getBasePointsNum(),
      categoryId,
      // 기본은 현재 선택 그룹(primaryGroup)의 선택값 (실제 저장은 페이지에서 그룹별로 처리)
      assignedStudentIds: Array.from(selectedByGroup[primaryGroup] || new Set(activeMembers.map(m => m.id))),
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true
    };

    // icon 필드 추가 (빈 문자열도 유효한 값으로 간주)
    if (icon !== undefined) {
      assignmentData.icon = icon;
      console.log('🔍 NewAssignmentModal - icon 필드 추가:', icon);
    } else {
      console.log('🔍 NewAssignmentModal - icon 필드 undefined, 추가하지 않음');
    }

    // notes 필드 추가 (빈 문자열도 유효한 값으로 간주)
    if (notes !== undefined) {
      assignmentData.notes = notes;
      console.log('🔍 NewAssignmentModal - notes 필드 추가:', notes);
    } else {
      console.log('🔍 NewAssignmentModal - notes 필드 undefined, 추가하지 않음');
    }

    // 과제 보상 필드 추가 - rewardEnabled는 항상 저장 (false도 저장)
    // 코인, 보상 카드, 완료 코드, 완료 메시지 중 하나라도 있어야 true 유지
    // 모두 없으면 false로 저장
    const hasRewards = (getRewardCoinsNum() > 0) || (rewardItemCardIds.length > 0);
    const hasCompletionCode = rewardCode && rewardCode.trim().length > 0;
    const hasCompletionMessage = rewardMessage && rewardMessage.trim().length > 0;
    const shouldEnableReward = rewardEnabled && (hasRewards || hasCompletionCode || hasCompletionMessage);
    
    assignmentData.rewardEnabled = shouldEnableReward;
    
    if (shouldEnableReward) {
      if (rewardCode && rewardCode.trim()) {
        assignmentData.rewardCode = rewardCode;
      }
      if (rewardMessage && rewardMessage.trim()) {
        assignmentData.rewardMessage = rewardMessage;
      }
      // 코인이 0이어도 명시적으로 전달 (서비스에서 삭제 처리)
      assignmentData.rewardCoins = getRewardCoinsNum();
      // 카드가 0개여도 명시적으로 전달 (서비스에서 삭제 처리)
      assignmentData.rewardItemCardIds = rewardItemCardIds;
      console.log('🔍 NewAssignmentModal - 과제 보상 필드 추가:', {
        rewardEnabled: shouldEnableReward,
        rewardCode,
        rewardMessage,
        rewardCoins: getRewardCoinsNum(),
        rewardItemCardIds,
        selectedRewardCards: selectedRewardCards.map(card => ({ id: card.itemCardId, name: card.name, category: card.category }))
      });
    } else {
      console.log('🔍 NewAssignmentModal - 과제 보상 비활성화됨:', {
        rewardEnabled: rewardEnabled,
        hasRewards: hasRewards,
        hasCompletionCode: hasCompletionCode,
        hasCompletionMessage: hasCompletionMessage
      });
    }

    console.log('🔍 NewAssignmentModal - 최종 assignmentData:', assignmentData);
    console.log('🔍 NewAssignmentModal - icon 값:', assignmentData.icon);
    console.log('🔍 NewAssignmentModal - notes 값:', assignmentData.notes);

    const assignment: Assignment = assignmentData;
    const payload = getSelectedGroupsPayload();
    onSave(assignment, payload, mode);
  };

  const grouped = useMemo(() => {
    // 동적 컬럼 수에 맞춰 멤버들을 균등하게 분배
    const count = activeMembers.length;
    const columnCount = count <= 25 ? Math.ceil(count / 5) : Math.ceil(count / 6);
    const perCol = Math.ceil(count / columnCount);
    const columns: ClassMember[][] = [];
    activeMembers.forEach((m, idx) => {
      const colIdx = Math.floor(idx / perCol);
      if (!columns[colIdx]) columns[colIdx] = [];
      columns[colIdx].push(m);
    });
    return columns;
  }, [activeMembers]);

  // 동적 컬럼 수 계산 - 멤버 수에 따라 더 적절한 열 수 계산
  const columnCount = activeMembers.length <= 25 ? Math.ceil(activeMembers.length / 5) : Math.ceil(activeMembers.length / 6);

  if (!isOpen) return null;
  return (
    <>
      <ModalOverlay onClick={handleClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <Title>
            {mode === 'new' && '일반 과제 생성'}
            {mode === 'copy' && '복제 과제 만들기'}
            {mode === 'edit' && '과제 편집하기'}
          </Title>

          {/* 섹션 1: 과제 이름, 과제 설명 */}
          <FormSection>
            <Field>
              <Label>과제 이름</Label>
              <Input 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                onBlur={() => handleTitleComplete(title)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTitleComplete(title);
                    // 상태 업데이트 후 포커스 이동을 위해 setTimeout 사용
                    setTimeout(() => {
                      const nextElement = document.querySelector('[tabindex="2"]') as HTMLElement;
                      if (nextElement) nextElement.focus();
                    }, 0);
                  }
                }}
                placeholder="예: 일기 쓰기"
                tabIndex={1}
              />
            </Field>

            <Field>
              <Label style={{ textAlign: 'center' }}>과제 설명 (선택사항)</Label>
              <NotesTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="과제에 대한 추가 설명이나 메모를 입력하세요..."
                rows={3}
              />
            </Field>
          </FormSection>

          {/* 섹션 2: 과제 완료 보상 */}
          <FormSection>
            {/* 과제 완료 보상 토글 */}
            <Field>
              <ToggleRow>
                <Label>과제 완료 보상</Label>
                <ToggleSwitch 
                  $isOn={rewardEnabled}
                  onClick={() => setRewardEnabled(!rewardEnabled)}
                >
                  <ToggleSlider $isOn={rewardEnabled}>
                    {rewardEnabled ? 'ON' : 'OFF'}
                  </ToggleSlider>
                </ToggleSwitch>
              </ToggleRow>
            </Field>

            {/* 과제 보상 섹션 */}
            {rewardEnabled && (
              <>
              {/* 1행: 과제 완료 코드 */}
              <Field>
                <Label>과제 완료 코드</Label>
                <Input
                  type="text"
                  value={rewardCode}
                  onChange={(e) => setRewardCode(e.target.value)}
                  placeholder="예: ABC123"
                />
              </Field>

              {/* 과제 완료 메시지 */}
              <Field>
                <Label>과제 완료 메시지</Label>
                <NotesTextarea
                  value={rewardMessage}
                  onChange={(e) => setRewardMessage(e.target.value)}
                  placeholder="과제 완료 시 멤버에게 표시할 안내 메시지를 입력하세요..."
                  rows={2}
                />
              </Field>

              {/* 2행: 획득 코인 */}
              <Field>
                <LabelRow>
                  <Label>획득 코인</Label>
                  <IncButtons>
                    <ResetBtn type="button" aria-label="코인 초기화" onClick={() => setRewardCoinsDisplay('0')}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </ResetBtn>
                    <RoundBtn type="button" aria-label="코인 빼기" onClick={() => {
                      const n = getRewardCoinsNum();
                      if (n > 0) setRewardCoinsDisplay(String(Math.max(0, n - 10)));
                    }}>
                      <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </RoundBtn>
                    <RoundBtn type="button" aria-label="코인 더하기" onClick={() => {
                      setRewardCoinsDisplay(String(getRewardCoinsNum() + 10));
                    }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </RoundBtn>
                  </IncButtons>
                </LabelRow>
                <Input
                  type="number"
                  min={0}
                  value={rewardCoinsDisplay}
                  onChange={(e) => setRewardCoinsDisplay(e.target.value)}
                  onBlur={() => { if (rewardCoinsDisplay === '') setRewardCoinsDisplay('0'); }}
                />
              </Field>

              {/* 3행: 획득 카드 설정 */}
              <Field>
                <Label>획득 카드 설정 (과제보상 아이템)</Label>
                <RewardCardsContainer>
                  {selectedRewardCards.map((itemCard) => (
                    <RewardCardItem key={itemCard.itemCardId}>
                      <RewardCardIconArea>
                        {itemCard.cardImage === 'default' || rewardCardImageErrors.has(itemCard.itemCardId) ? (
                          <RewardCardPlaceholderIcon>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          </RewardCardPlaceholderIcon>
                        ) : itemCard.cardImage && /^\d+$/.test(itemCard.cardImage) ? (
                          <img src={`/CardIcon/IconBasic/IconBasic-${itemCard.cardImage}.png`} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                        ) : itemCard.cardImage ? (
                          <img src={itemCard.cardImage} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                        ) : itemCard.icon ? (
                          <img src={itemCard.icon} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                        ) : (
                          <RewardCardPlaceholderIcon>
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                              <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                          </RewardCardPlaceholderIcon>
                        )}
                      </RewardCardIconArea>
                      <RewardCardName>{itemCard.name}</RewardCardName>
                      <RewardCardMemo>{itemCard.memo}</RewardCardMemo>
                      <RemoveCardButton
                        onClick={() => {
                          const newRewards = rewardItemCardIds.filter(id => id !== itemCard.itemCardId);
                          setRewardItemCardIds(newRewards);
                          loadRewardCards(newRewards);
                        }}
                      >
                        ×
                      </RemoveCardButton>
                    </RewardCardItem>
                  ))}
                  <AddCardButton
                    disabled={user?.userType === 'free' || user?.userType === 'basic'}
                    onClick={() => {
                      if (user?.userType === 'free' || user?.userType === 'basic') {
                        setLicenseLimitMessage('프리/베이직 플랜에서는 아이템 카드를 과제 보상으로 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                        setShowLicenseLimitModal(true);
                        return;
                      }
                      if (onOpenItemCardModal) {
                        onOpenItemCardModal(rewardItemCardIds, (selectedIds) => {
                          setRewardItemCardIds(selectedIds);
                          loadRewardCards(selectedIds);
                        });
                      }
                    }}
                  >
                    <AddRewardIcon>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </AddRewardIcon>
                    <AddRewardText>아이템 카드 추가</AddRewardText>
                  </AddCardButton>
                </RewardCardsContainer>
              </Field>
            </>
            )}
          </FormSection>

          {/* 섹션 3: 마감날짜, 획득포인트, 카테고리 */}
          <FormSection>
            <FieldRow>
            <Field>
              <LabelRow>
                <Label>마감 날짜</Label>
                <IncButtons>
                  <ResetBtn type="button" aria-label="날짜 초기화" onClick={() => {
                    const today = new Date();
                    const yyyy = today.getFullYear();
                    const mm = String(today.getMonth() + 1).padStart(2, '0');
                    const dd = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${yyyy}-${mm}-${dd}`;
                    setDueDate(todayStr);
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </ResetBtn>
                  <RoundBtn type="button" aria-label="하루 빼기" onClick={() => {
                    if (!dueDate) return; const d = new Date(dueDate); d.setDate(d.getDate() - 1); setDueDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                  <RoundBtn type="button" aria-label="하루 더하기" onClick={() => {
                    if (!dueDate) return; const d = new Date(dueDate); d.setDate(d.getDate() + 1); setDueDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                </IncButtons>
              </LabelRow>
              <DateInputWrapper>
                {dueDate && (
                  <WeekdayBadge>
                    {['일','월','화','수','목','금','토'][new Date(dueDate).getDay()]}
                    {(() => {
                      const today = new Date();
                      const due = new Date(dueDate);
                      
                      // 날짜만 비교하기 위해 시간 정보 제거
                      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const dueDateOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
                      
                      const diffTime = dueDateOnly.getTime() - todayDate.getTime();
                      const diffDays = diffTime / (1000 * 60 * 60 * 24);
                      
                      if (diffDays === 0) return ' (오늘)';
                      if (diffDays === 1) return ' (+1)';
                      if (diffDays === 2) return ' (+2)';
                      if (diffDays === 3) return ' (+3)';
                      if (diffDays === 4) return ' (+4)';
                      if (diffDays === 5) return ' (+5)';
                      if (diffDays === 6) return ' (+6)';
                      if (diffDays === 7) return ' (+7)';
                      if (diffDays > 7) return ` (+${diffDays})`;
                      if (diffDays === -1) return ' (-1)';
                      if (diffDays === -2) return ' (-2)';
                      if (diffDays === -3) return ' (-3)';
                      if (diffDays < -3) return ` (${diffDays})`;
                      return '';
                    })()}
                  </WeekdayBadge>
                )}
                <DateInput 
                  type="date" 
                  value={dueDate} 
                  onChange={e => setDueDate(e.target.value)}
                />
              </DateInputWrapper>
            </Field>
            <Field>
              <LabelRow>
                <Label>획득 포인트</Label>
                <IncButtons>
                  <ResetBtn type="button" aria-label="포인트 초기화" onClick={() => {
                    setBasePointsDisplay('0');
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </ResetBtn>
                  <RoundBtn type="button" aria-label="포인트 빼기" onClick={() => {
                    const n = getBasePointsNum();
                    if (n > 0) setBasePointsDisplay(String(Math.max(0, n - defaultPoints)));
                  }}>
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                  <RoundBtn type="button" aria-label="포인트 더하기" onClick={() => {
                    setBasePointsDisplay(String(getBasePointsNum() + defaultPoints));
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                </IncButtons>
              </LabelRow>
              <Input 
                type="number" 
                min={0} 
                value={basePointsDisplay} 
                onChange={e => setBasePointsDisplay(e.target.value)}
                onBlur={() => { if (basePointsDisplay === '') setBasePointsDisplay('0'); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    // 다음 필드로 이동
                    const nextElement = document.querySelector('[tabindex="3"]') as HTMLElement;
                    if (nextElement) nextElement.focus();
                  }
                  handleTabKey(e, 2);
                }}
                tabIndex={2}
              />
            </Field>
          </FieldRow>

          <Field>
            <Label>카테고리</Label>
            <CategorySelectContainer>
              <CategorySelectButton 
                onClick={() => {
                  setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                  if (!isCategoryDropdownOpen) {
                    setSelectedCategoryIndex(0); // 드롭다운 열 때 선택 인덱스 초기화
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!isCategoryDropdownOpen) {
                      // 드롭다운 열기
                      setIsCategoryDropdownOpen(true);
                      setSelectedCategoryIndex(0);
                    } else {
                      // 현재 선택된 항목 확정
                      if (selectedCategoryIndex === 0) {
                        handleCategorySelect('');
                      } else {
                        const selectedCategory = categories[selectedCategoryIndex - 1];
                        handleCategorySelect(selectedCategory.id);
                      }
                    }
                  } else if (e.key === 'ArrowDown' && isCategoryDropdownOpen) {
                    e.preventDefault();
                    const totalOptions = categories.length + 1;
                    setSelectedCategoryIndex(prev => (prev + 1) % totalOptions);
                  } else if (e.key === 'ArrowUp' && isCategoryDropdownOpen) {
                    e.preventDefault();
                    const totalOptions = categories.length + 1;
                    setSelectedCategoryIndex(prev => prev === 0 ? totalOptions - 1 : prev - 1);
                  } else if (e.key === 'Escape' && isCategoryDropdownOpen) {
                    e.preventDefault();
                    setIsCategoryDropdownOpen(false);
                  } else if (e.key === 'Tab') {
                    handleTabKey(e, 3);
                  }
                }}
                type="button"
                tabIndex={3}
              >
                {categoryId ? (
                  <CategoryOption>
                    <ColorDot color={categories.find(cat => cat.id === categoryId)?.color || 'rgba(162, 162, 162, 1)'} />
                    <span>{categories.find(cat => cat.id === categoryId)?.name}</span>
                  </CategoryOption>
                ) : (
                  <CategoryOption>
                    <ColorDot color="rgba(162, 162, 162, 1)" />
                    <span>선택 안 함</span>
                  </CategoryOption>
                )}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </CategorySelectButton>
              
              {isCategoryDropdownOpen && (
                <>
                  <DropdownOverlay onClick={() => setIsCategoryDropdownOpen(false)} />
                  <CategoryDropdown className="category-dropdown">
                    <CategoryOption 
                      onClick={() => handleCategorySelect('')}
                      isSelected={selectedCategoryIndex === 0}
                    >
                      <ColorDot color="rgba(162, 162, 162, 1)" />
                      <span>선택 안 함</span>
                    </CategoryOption>
                    {categories.map((cat, index) => (
                      <CategoryOption 
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.id)}
                        isSelected={selectedCategoryIndex === index + 1}
                      >
                        <ColorDot color={cat.color} />
                        <span>{cat.name}</span>
                      </CategoryOption>
                    ))}
                  </CategoryDropdown>
                </>
              )}
            </CategorySelectContainer>
          </Field>
          </FormSection>

          {/* 섹션 4: 과제를 부여할 멤버 선택 */}
          <FormSection>
            <SelectHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>과제를 부여할 멤버 선택</span>
              {hoveredStudent && (
                <SelectedStudentBadge key={hoveredStudent.id + Date.now()}>
                  {hoveredStudent.name}
                </SelectedStudentBadge>
              )}
            </div>
            <SelectAllButton onClick={toggleAll}>
              {activeSelected.size === activeMembers.length ? '전체 해제' : '전체 선택'}
            </SelectAllButton>
          </SelectHeader>

          {normalizedGroups.length > 1 && (
            <GroupControls>
              <GroupTabs>
                {normalizedGroups.map((g) => (
                  <GroupTabButton
                    key={g}
                    type="button"
                    $active={activeGroup === g}
                    onClick={async () => {
                      setActiveGroup(g);
                      await ensureMembersLoaded(g);
                      await ensureAssignmentSelectionLoaded(g);
                    }}
                  >
                    {getGroupDisplayName(g)}
                  </GroupTabButton>
                ))}
              </GroupTabs>
            </GroupControls>
          )}

          <StudentsGrid $columnCount={columnCount}>
            {grouped.map((col, colIdx) => (
              <Column key={colIdx}>
                {col.map(m => (
                  <div key={m.id} style={{ position: 'relative' }}>
                    <StudentButton
                      onClick={() => {
                        toggle(m.id);
                        setHoveredStudent(hoveredStudent?.id === m.id ? null : m);
                      }}
                      aria-pressed={activeSelected.has(m.id)}
                      title={m.name}
                    >
                      <span className="num">{String(m.number).padStart(2, '0')}</span>
                      <span className="name">{m.name}</span>
                    </StudentButton>
                  </div>
                ))}
              </Column>
            ))}
          </StudentsGrid>
          </FormSection>

          <Buttons>
            <CancelButton data-variant="modal-secondary" onClick={handleClose}>취소</CancelButton>
            <SaveButton 
              onClick={handleSave} 
              onKeyDown={e => handleTabKey(e, 4)}
              tabIndex={4}
              disabled={!title.trim() || Object.keys(getSelectedGroupsPayload()).length === 0}
            >
              저장
            </SaveButton>
          </Buttons>
        </ModalContent>
      </ModalOverlay>

      {/* 변경사항 확인 모달 */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          onCancel();
        }}
        title={['변경된 데이터가 있습니다.', '팝업을 닫으시겠습니까?']}
        confirmText="닫기"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-pending)"
      />

      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />
    </>
  );
};

export default NewAssignmentModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;
const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 30px 20px;
  width: 100%;
  max-width: 720px;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  margin: 150px auto 20px auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
    margin: 150px auto 20px auto;
  }
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;
const Title = styled.h3`
  margin: 0 0 16px; text-align: center; font-size: 1.2rem; color: var(--color-text-primary);
`;

const FormSection = styled.div`
  background: var(--color-section-bg);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid var(--color-section-border);
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px;
  
  ${FormSection} &:last-child {
    margin-bottom: 0;
  }
`;
const FieldRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;
const Input = styled.input`
  padding: 8px 10px; 
  border: 1px solid var(--color-border); 
  border-radius: 6px; 
  font-size: 1rem; 
  min-width: 0; 
  height: 38px; 
  box-sizing: border-box;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const LabelRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;
const IncButtons = styled.div`
  display: flex; gap: 2px; flex-shrink: 0;
`;
const RoundBtn = styled.button`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 2px 6px;
    height: 24px;
    min-height: 24px;
    font-size: 0.8rem;
  }

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    color: var(--color-text-primary);
  }
`;

const ResetBtn = styled(RoundBtn)`
  svg {
    color: var(--color-text-primary);
    
    rect,
    path {
      stroke: var(--color-text-primary);
    }
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--color-surface);
  height: 38px;
  box-sizing: border-box;
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const DateInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  
  /* 브라우저 기본 달력 아이콘 스타일링 - 다크 모드에서만 흰색으로 */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 1;
    filter: var(--date-calendar-icon-filter);
  }
  
  &::-moz-calendar-picker-indicator {
    cursor: pointer;
    opacity: 1;
    filter: var(--date-calendar-icon-filter);
  }
  
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
`;

const WeekdayBadge = styled.span`
  background-color: var(--weekday-badge-bg);
  color: var(--weekday-badge-text-color);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: var(--weekday-badge-text-weight);
  white-space: nowrap;
  flex-shrink: 0;
`;

const SelectHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin: 8px 0;
`;
const SelectAllButton = styled.button`
  padding: 6px 10px; background: var(--color-surface); border: 2px solid var(--color-border); border-radius: 8px; font-weight: 600; cursor: pointer;
  color: var(--color-text-primary);
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
`;

const StudentsGrid = styled.div<{ $columnCount: number }>`
  display: grid; 
  grid-template-columns: repeat(${props => props.$columnCount}, auto);
  gap: 6px; 
  align-items: start; 
  justify-items: center;
  justify-content: center;
  padding: 8px; 
  border: 2px solid var(--color-border); 
  border-radius: 8px; 
  background: var(--color-panel);
  width: 100%;
  max-height: 300px;
  overflow-x: auto;
  overflow-y: auto;
  margin: 0 auto;
`;

const Column = styled.div`
  display: grid; 
  grid-auto-rows: minmax(36px, auto); 
  gap: 6px;
`;

const StudentButton = styled.button<{ disabled?: boolean }>`
  display: flex; 
  align-items: center; 
  gap: 4px; 
  padding: 6px 8px; 
  background: var(--color-surface); 
  border: 1px solid var(--color-border); 
  border-radius: 8px; 
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  box-shadow: none;
  
  .num { 
    font-weight: 700; 
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-border)'};
    font-size: 16px;
    flex-shrink: 0;
  }
  .name { 
    display: inline; 
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-muted)'};
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }
  
  @media (max-width: 600px) { 
    .name { 
      font-size: 14px;
    }
  }
  
  @media (max-width: 520px) { 
    justify-content: center;
    .name { display: none; } 
  }
  
  /* 선택된 상태 */
  &[aria-pressed="true"] {
    background: var(--color-surface);
    border-color: var(--color-accent-text);
    border-width: 1px;
    box-shadow: none;

    .num {
      color: var(--color-accent-text);
      font-weight: 700;
    }
    .name {
      color: var(--color-text-primary);
      font-weight: 600;
    }
  }
  
  /* 호버 효과 */
  &:hover {
    background-color: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-border)'};
  }
  
  /* 선택된 상태에서 호버 시 테두리 유지 */
  &[aria-pressed="true"]:hover {
    border-color: var(--color-accent-text);
    border-width: 1px;
    box-shadow: none;
  }
  
  &:focus-visible { 
    outline: 2px solid #000; 
    outline-offset: 2px; 
  }
`;

const SelectedStudentBadge = styled.span`
  background-color: var(--color-student-badge-bg);
  color: var(--color-student-badge-text);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 8px;
  animation: slideInFromRight 0.3s ease-out, fadeOut 0.5s ease-in 1.5s forwards;
  
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(0);
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const GroupControls = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin: 10px 0 6px;
  flex-wrap: wrap;
`;

const GroupTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const GroupTabButton = styled.button<{ $active: boolean }>`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: ${({ $active }) => ($active ? 'var(--color-accent-strong)' : 'var(--color-panel)')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--color-text-primary)')};
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
`;

// TargetGroups UI 제거: 전파는 "그룹별 학생 선택 여부"로 자동 결정

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CategorySelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CategorySelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  height: 38px;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  width: 100%;
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const CategoryOption = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  margin: 2px;
  &:hover {
    background-color: var(--color-surface-highlight);
  }
`;

const ColorDot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const CategoryDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: var(--modal-shadow);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
`;

const DropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const NotesTextarea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  width: 100%;
  text-align: center;
  min-height: 60px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
    text-align: center;
  }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

// 과제 보상 관련 스타일 컴포넌트
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ToggleSwitch = styled.button<{ $isOn: boolean }>`
  width: 70px;
  height: 28px;
  min-width: 70px;
  min-height: 28px;
  border-radius: 14px;
  background: ${props => props.$isOn ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ToggleSlider = styled.div<{ $isOn: boolean }>`
  width: 34px;
  height: 22px;
  border-radius: 11px;
  background: var(--color-surface);
  position: absolute;
  top: 3px;
  left: ${props => props.$isOn ? '34px' : '3px'};
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.$isOn ? 'var(--color-toggle-on-text)' : 'var(--color-toggle-off-text)'};
`;

const RewardCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  min-height: 220px;
  justify-content: center;
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const RewardCardItem = styled.div`
  width: 120px;
  height: 200px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  position: relative;
  box-sizing: border-box;
  transition: all 0.2s ease;

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RewardCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RewardCardPlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RewardCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RewardCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const RemoveCardButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--color-accent-strong);
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 1;

  /* 다크 모드: 흰색 배경에 회색 X */
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 1);
    color: #333333;
  }

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.8);
    transform: scale(1.1);
  }

  /* 다크 모드: 호버 시에도 흰색 유지 */
  :root:not([data-theme='light']) &:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddCardButton = styled.button<{ disabled?: boolean }>`
  width: 120px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px;
  background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-surface)'};
  border: 2px dashed ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  border-radius: 8px;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-text)'};
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-sizing: border-box;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background: var(--color-accent-soft);
    border-color: rgba(var(--color-accent-rgb), 0.8);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddRewardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border: 2px dashed var(--color-accent-strong);
  border-radius: 50%;
  background: var(--color-accent-soft);
  flex-shrink: 0;
`;

const AddRewardText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-text);
  text-align: center;
  line-height: 1.3;
  word-break: keep-all;
  flex-shrink: 0;
`;
